<script setup lang="ts">
import { onMounted, ref } from "vue";

interface Props {
  placeholder?: string;
  type?: string;
  icon?: string;
  focus?: boolean;
  label?: string;
  helpText?: string;
  borderless?: boolean;
}

const {
  placeholder = "",
  type = "text",
  icon = "",
  focus = false,
  label = "",
  helpText = "",
  borderless = false,
} = defineProps<Props>();

const model = defineModel<string>();

defineOptions({
  inheritAttrs: false,
});

const input = ref<HTMLInputElement | null>(null);
const isValid = ref<boolean>(true);

function onChange() {
  if (input.value) {
    model.value = input.value.value;
    isValid.value = input.value.checkValidity();
  }
}

onMounted(() => {
  if (focus && input.value) {
    input.value.focus();
  }
});

defineExpose({
  checkValidity: () => isValid.value,
  focus: () => input.value?.focus(),
});
</script>

<template>
  <label class="text-input" :class="{ borderless }">
    <div v-if="label" class="text-base-medium">{{ label }}</div>
    <div v-if="helpText" class="help text-base-regular">{{ helpText }}</div>

    <div class="text-input-container">
      <span v-if="icon" :class="icon" class="icon" />

      <input
        :type="type"
        :value="model"
        :placeholder="placeholder"
        @input="onChange"
        ref="input"
        v-bind="$attrs"
      />
    </div>
  </label>
</template>

<style scoped>
.text-input {
  display: inline-flex;
  flex-direction: column;
  gap: var(--numbers-8);

  .text-input-container {
    display: inline-flex;
    align-items: center;
    padding: var(--numbers-8);
    border: var(--stroke-width-md) solid var(--border-tertiary);
    border-radius: var(--numbers-4);
    box-shadow: 0 1px 2px rgb(from var(--border-tertiary) r g b / 20%);
    transition:
      border-color var(--animation-duration) var(--animation-easing),
      box-shadow var(--animation-duration) var(--animation-easing);

    .icon {
      margin: 0 var(--numbers-4);
      color: var(--content-primary);
    }

    input {
      width: 100%;
      min-width: 0;
      border: none;
      background-color: transparent;
      color: var(--content-primary);

      &:focus,
      &:focus-visible {
        box-shadow: none;
        outline: none;
      }

      &::placeholder {
        color: var(--content-disabled);
      }
    }

    &:has(input:focus) {
      box-shadow:
        0 0 0 0.25rem rgb(6 43 205 / 30%),
        0 0 0 0.125rem rgb(255 255 255 / 30%);
    }
  }

  /* Embedded inside a content row (e.g. an elicitation option), so the input
     matches the surrounding 14px text and keeps only its own focus affordance. */
  &.borderless {
    .text-input-container {
      padding: var(--numbers-2);
      border: none;
      border-radius: 0;
      box-shadow: none;
    }

    input {
      font-size: var(--numbers-14);
    }
  }

  .help {
    color: var(--content-tertiary);
  }
}
</style>
